import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';
import {
  IconButtonProps,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@material-ui/core';
import { Warning as WarningIcon } from '@material-ui/icons';

import { FetchError } from '../../common/api/api';
import SearchBox from '../../common/SearchBox';
import AppSnackbar from '../../common/AppSnackbar';
import StatusLabel from '../../common/StatusLabel';
import ErrorView from '../../common/views/ErrorView';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateHeader from '../../common/page-template/PageTemplateHeader';
import PageTemplateCardOver from '../../common/page-template/PageTemplateCardOver';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import { convertJiraStatus } from '../jira/Jira.hooks';
import JiraListFilter, {
  JiraListFilterOptions
} from './components/JiraListFilter';
import JiraExportButton from './components/JiraExportButton';
import JiraTemplateConfigButton from './components/JiraTemplateConfigButton';
import {
  JiraIssuesFilterOptions,
  useFindJiraConnect,
  useFindJiraIssues,
  useJiraConnectAPI
} from './JiraConnect.hooks';
import { JiraConnect, JiraIssue } from './JiraConnect.interface';

const StyledTableCell = styled(TableCell)`
  white-space: normal;
  word-wrap: break-word;
`;

const JiraListExportPage: React.FC = () => {
  // page is a state instead of a query param
  // since jira issues search pagination has changed
  // and the next pages are only accessible via a `nextPageToken`
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [order, setOrder] = useQueryParam(
    'order',
    withDefault(StringParam, undefined)
  );
  const [search, setSearch] = useQueryParam(
    'search',
    withDefault(StringParam, undefined)
  );
  const [orderDirection, setOrderDirection] = useQueryParam(
    'direction',
    withDefault(StringParam, undefined)
  );
  const [error, setError] = useState<FetchError | undefined>();
  const [filter, setFilter] = useState<JiraListFilterOptions>({});
  const [jiraConnect, setJiraConnect] = useState<JiraConnect | undefined>();
  const { isAvailable } = useJiraConnectAPI();
  const [
    { data: foundJiraConnect, error: findJiraConnectError },
    findJiraConnect
  ] = useFindJiraConnect();
  const [
    { data, isLoading, error: findIssuesError },
    findIssues,
    getFilterOptions
  ] = useFindJiraIssues();
  // only disable when true to properly fallback to defaults
  const iconButtonProps: IconButtonProps | undefined = isLoading
    ? { disabled: true }
    : undefined;

  const handleSort = (column: string) => {
    // reset page to avoid pagination mismatch since jql will be updated
    setPage(0);
    setOrder(column);
    setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && orderDirection === 'DESC' ? 'desc' : 'asc';
  };

  const options: JiraIssuesFilterOptions = useMemo(() => {
    return getFilterOptions({
      page,
      limit,
      sort: order,
      direction: orderDirection,
      title: search,
      filter,
      data: { expand: ['renderedFields'] }
    });
  }, [page, limit, order, orderDirection, search, filter, getFilterOptions]);

  useEffect(() => {
    (async () => {
      if (!isAvailable) {
        return;
      }

      const { data } = await findIssues(options);
      // this only serves as a fallback in case the page state was not reset
      if (data?.page !== undefined && data.page !== options.page) {
        setPage(data.page);
      }
    })();
  }, [options, isAvailable, findIssues]);

  useEffect(() => {
    isAvailable && findJiraConnect();
  }, [isAvailable, findJiraConnect]);

  useEffect(() => {
    foundJiraConnect && setJiraConnect(foundJiraConnect);
  }, [foundJiraConnect]);

  useEffect(() => {
    setError(findIssuesError || findJiraConnectError);
  }, [findIssuesError, findJiraConnectError]);

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <h2>JIRA Issues Export</h2>
        {isAvailable && (
          <>
            <SearchBox
              value={search}
              onSearch={value => {
                // reset page to avoid pagination mismatch since jql will be updated
                setPage(0);
                setSearch(value);
              }}
            >
              <JiraListFilter
                onFilter={filter => {
                  // reset page to avoid pagination mismatch since jql will be updated
                  setPage(0);
                  setFilter(filter);
                }}
              />
            </SearchBox>
            <JiraTemplateConfigButton
              disabled={!jiraConnect}
              jiraConnect={jiraConnect}
              setJiraConnect={setJiraConnect}
            />
            <JiraExportButton options={options} jiraConnect={jiraConnect} />
          </>
        )}
      </PageTemplateHeader>

      {!isAvailable && (
        <PageTemplateDashboard>
          <ErrorView
            title='JIRA Connect Not Available'
            description='JIRA Connect is required for this page to function properly.'
            icon={<WarningIcon color='disabled' fontSize='inherit' />}
          />
        </PageTemplateDashboard>
      )}
      {isAvailable && (
        <PageTemplateCardOver>
          <TableContainer component={Paper}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={order === 'key'}
                      direction={getSortDirection('key')}
                      onClick={() => handleSort('key')}
                    >
                      Issue Key
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={order === 'summary'}
                      direction={getSortDirection('summary')}
                      onClick={() => handleSort('summary')}
                    >
                      Title
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={order === 'assignee'}
                      direction={getSortDirection('assignee')}
                      onClick={() => handleSort('assignee')}
                    >
                      Assignee
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={order === 'status'}
                      direction={getSortDirection('status')}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={6} padding='none'>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading &&
                  data &&
                  Array.isArray(data.data) &&
                  data.data.map((issue: JiraIssue) => (
                    <TableRow
                      hover
                      key={issue.key}
                      onClick={() => {
                        if (typeof AP?.navigator?.go === 'function') {
                          AP.navigator.go('issue', { issueKey: issue.key });
                        }
                      }}
                    >
                      <TableCell>{issue.key}</TableCell>
                      <StyledTableCell>{issue.fields.summary}</StyledTableCell>
                      <TableCell>
                        {issue.fields.assignee &&
                          issue.fields.assignee.displayName}
                      </TableCell>
                      <TableCell>
                        <StatusLabel
                          status={convertJiraStatus(issue.fields.status.name)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component='div'
            count={data?.total || 0}
            rowsPerPage={data?.limit || 100}
            page={options.page || 0} // use computed page
            SelectProps={{ disabled: isLoading }}
            backIconButtonProps={iconButtonProps}
            nextIconButtonProps={iconButtonProps}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setLimit(+e.target.value);
              setPage(0);
            }}
          />
        </PageTemplateCardOver>
      )}

      <AppSnackbar
        severity='error'
        open={!!error}
        onClose={() => setError(undefined)}
      >
        {error?.message}
      </AppSnackbar>
    </PageTemplate>
  );
};

export default JiraListExportPage;
